import { Box, CircularProgress } from '@mui/material';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import DropdownSelect from '../../components/DropdownSelect';
import MultiSelectWithAll from '../../components/MultiSelectWithAll';
import { ButtonWrapper, RoundedButton, SearchInputWrapper } from './styled';
import { academicYears } from '../../constants/academicYears';
import { useSchoolClassesConfigHook } from '../common/hooks/useSchoolClassesConfig';
import DailyBookTable from './components/DailyBookTable';
import ToastMessage from '../../components/ToastMessage';
import { SnackbarSeverityType } from '../../enums';
import {
  fetchDayWiseFeeBookReport,
  transformDayWiseFeeBookReport
} from './utils/DailyFeeBookUtils';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Dayjs } from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers';
import CustomDatePicker from '../../components/DatePicker';
import { isEmpty } from 'lodash';
import moment from 'moment';
import { downloadReport } from '../../utils/reportUtils';
import { PaperWrapper, Title } from '../common/styled';
import { RootState } from '../../redux/store';

const DEFAULT_OPTION = { value: 'All', label: 'All' };

const DailyBookPage = () => {
  const [selectedAcademicYear, setSelectedAcademicYear] =
    useState<string>('All');
  const [selectedGrade, setSelectedGrade] = useState<string>('All');
  const [selectedDivision, setSelectedDivision] = useState<string>('All');
  const [selectedComponents, setSelectedComponents] = useState<string[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [DayWiseFeeReportData, setDayWiseFeeReportData] = useState<any>([]);

  const { schoolConfig } = useSelector(
    (state: RootState) => state.schoolConfig
  );
  const dailyFeeBookReportConfig =
    schoolConfig?.featureConfigs?.paymentReports?.metaData?.dailyFeeBookReport;
  const showComponentsFilter =
    dailyFeeBookReportConfig?.enabled === true &&
    dailyFeeBookReportConfig?.showComponentsFilter === true;
  const componentOptions = (dailyFeeBookReportConfig?.components ?? []).map(
    (c: string) => ({ value: c, label: c })
  );
  const columnsToHide: string[] = dailyFeeBookReportConfig?.columnsToHide ?? [];

  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState<SnackbarSeverityType>(
    SnackbarSeverityType.success
  );

  const [fromDate, setFromDate] = useState<Dayjs | null>();
  const [toDate, setToDate] = useState<Dayjs | null>();

  const { gradeOptions, divisionOptions } = useSchoolClassesConfigHook();

  const gradeDropdownOptions = [
    { value: 'All', label: 'All' },
    ...gradeOptions
  ];
  const divisionDropdownOptions = [
    { value: 'All', label: 'All' },
    ...divisionOptions
  ];

  const academicYearOptions = [
    { value: 'All', label: 'All' },
    ...academicYears
  ];

  const handleCloseToast = () => {
    setToastOpen(false);
  };

  const showToast = (message: string, severity: SnackbarSeverityType) => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  };

  const feeReportParams = {
    fromDate: fromDate ? fromDate.format('YYYY-MM-DD') : '',
    toDate: toDate ? toDate.format('YYYY-MM-DD 23:59:59') : '',
    academicYear: selectedAcademicYear === 'All' ? '' : selectedAcademicYear,
    grade: selectedGrade === 'All' ? '' : selectedGrade,
    division: selectedDivision === 'All' ? '' : selectedDivision,
    components: selectedComponents.length > 0 ? selectedComponents : undefined
  };

  const handleSearch = async () => {
    setIsSearching(true);
    setDayWiseFeeReportData([]);
    try {
      const response = await fetchDayWiseFeeBookReport(feeReportParams);
      const transformedDayWiseReportData = transformDayWiseFeeBookReport(
        response as any
      );

      setDayWiseFeeReportData(transformedDayWiseReportData);

      showToast(
        'Successfully fetched daily fee book report',
        SnackbarSeverityType.success
      );
    } catch (error: any) {
      showToast(
        error.errors?.[0].message || 'Error fetching daily fee book report',
        SnackbarSeverityType.error
      );
    } finally {
      setIsSearching(false);
      setHasSearched(true);
    }
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const response = await fetchDayWiseFeeBookReport(feeReportParams);

      const downloadAbleFeeReportData = transformDayWiseFeeBookReport(
        response as any
      );

      if (isEmpty(downloadAbleFeeReportData)) {
        showToast(
          'There is no data available to download as an Excel file.',
          SnackbarSeverityType.error
        );
        return;
      }

      const filteredDownloadData =
        columnsToHide.length > 0
          ? downloadAbleFeeReportData.map((row: Record<string, any>) => {
              const filtered = { ...row };
              columnsToHide.forEach((col) => delete filtered[col]);
              return filtered;
            })
          : downloadAbleFeeReportData;

      const fileName = `Day Wise Fee Book Report -${moment().format(
        'DD-MM-YYYY_HH-mm-ss'
      )}`;

      downloadReport(
        filteredDownloadData,
        fileName,
        'Day Wise Fee Book Report',
        showToast
      );

      showToast('Report downloaded successfully', SnackbarSeverityType.success);
    } catch (error: any) {
      showToast(
        error.errors?.[0].message || 'Error downloading daily fee book report',
        SnackbarSeverityType.error
      );
    } finally {
      setIsDownloading(false);
    }
  };

  const disableSearchButtons =
    isSearching || isEmpty(fromDate) || isEmpty(toDate);
  const disableDownloadButtons =
    isDownloading || isSearching || isEmpty(fromDate) || isEmpty(toDate);

  return (
    <Box>
      <Title>Date Wise Fee Book Report</Title>
      <PaperWrapper>
        <SearchInputWrapper>
          <DropdownSelect
            label='Academic Year'
            placeholder='Select Academic Year'
            name='academicYear'
            value={selectedAcademicYear}
            options={academicYearOptions}
            onChange={(event) => {
              setSelectedAcademicYear(event.target.value as string);
              setHasSearched(false);
            }}
            hasTopLabel
          />
          <DropdownSelect
            label='Grade'
            placeholder='Select Grade'
            name='grade'
            value={selectedGrade}
            options={gradeDropdownOptions}
            onChange={(event) => {
              setSelectedGrade(event.target.value as string);
              setHasSearched(false);
            }}
            hasTopLabel
          />
          <DropdownSelect
            label='Division'
            placeholder='Select Division'
            name='Division'
            value={selectedDivision}
            options={divisionDropdownOptions}
            onChange={(event) => {
              setSelectedDivision(event.target.value as string);
              setHasSearched(false);
            }}
            hasTopLabel
          />
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <CustomDatePicker
              label='From Date'
              required
              onChange={(newValue: any) => {
                setFromDate(newValue);
                setHasSearched(false);
              }}
              value={fromDate}
              width='100%'
            />
            <CustomDatePicker
              label='To Date'
              required
              onChange={(newValue: any) => {
                setToDate(newValue);
                setHasSearched(false);
              }}
              value={toDate}
              width='100%'
            />
          </LocalizationProvider>
          {showComponentsFilter && (
            <MultiSelectWithAll
              label='Component'
              required={false}
              options={componentOptions}
              selected={selectedComponents}
              onChange={(values) => {
                const withoutAll = values.filter(
                  (v) => v !== DEFAULT_OPTION.value
                );
                setSelectedComponents(withoutAll);
                setHasSearched(false);
              }}
            />
          )}
          <ButtonWrapper>
            <RoundedButton
              onClick={handleSearch}
              disabled={disableSearchButtons}
            >
              {isSearching ? (
                <CircularProgress size={24} color='inherit' />
              ) : (
                'View Report'
              )}
            </RoundedButton>
            <RoundedButton
              onClick={handleDownload}
              disabled={disableDownloadButtons}
            >
              {isDownloading ? (
                <CircularProgress size={24} color='inherit' />
              ) : (
                'Download Report'
              )}
            </RoundedButton>
          </ButtonWrapper>
        </SearchInputWrapper>
      </PaperWrapper>
      {hasSearched && !isSearching && (
        <DailyBookTable
          DailyFeeBookReportData={DayWiseFeeReportData}
          columnsToHide={columnsToHide}
        />
      )}
      <ToastMessage
        open={toastOpen}
        message={toastMessage}
        severity={toastSeverity}
        onClose={handleCloseToast}
      />
    </Box>
  );
};

export default DailyBookPage;
